<template>
  <editor-content
    :editor="editor"
    :class="{ 'editor-sized': fontSize }"
    :style="fontSize ? { '--editor-font-size': fontSize } : {}"
  />
</template>

<script setup>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-3';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  // Optional content font size (e.g. '0.85rem'). When set, overrides the
  // global `p { font-size }` for this editor's text. Omit to keep defaults.
  fontSize: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const editor = ref(null);

watch(() => props.modelValue, (value) => {
  const isSame = editor.value.getHTML() === value;
  // For JSON comparison, use: 
  // const isSame = JSON.stringify(editor.value.getJSON()) === JSON.stringify(value);

  if (!isSame) {
    editor.value.commands.setContent(value, false);
  }
});

onMounted(() => {
  editor.value = new Editor({
    extensions: [
      StarterKit,
    ],
    content: props.modelValue,
    onUpdate: () => {
      // For HTML
      emit('update:modelValue', editor.value.getHTML());

      // For JSON
      // emit('update:modelValue', editor.value.getJSON());
    },
  });
});

onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});
</script>

<style scoped>
/* Opt-in font sizing: only applies when a fontSize prop is given, so other
   EditorTipTap usages keep their defaults. Targets the paragraphs/list items
   directly to beat the global `p { font-size: 3rem }` in default.scss. */
.editor-sized :deep(.ProseMirror),
.editor-sized :deep(.ProseMirror p),
.editor-sized :deep(.ProseMirror li),
.editor-sized :deep(.ProseMirror h1),
.editor-sized :deep(.ProseMirror h2),
.editor-sized :deep(.ProseMirror h3) {
  font-size: var(--editor-font-size);
  line-height: 1.4;
  margin-bottom: 0.5rem;
}
</style>
